import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AnalyticsService } from '../../../services/analytics.service';
import { RelatorioProdutoVendidoItem } from '../../../models/financeiro.model';
import { formatarNumeroPtBR } from '../../../core/utils/moeda.util';

@Component({
  selector: 'app-relatorio-produtos-vendidos',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [DatePipe],
  templateUrl: './relatorio-produtos-vendidos.component.html',
  styleUrl: './relatorio-produtos-vendidos.component.css'
})
export class RelatorioProdutosVendidosComponent {
  private analyticsService = inject(AnalyticsService);
  private toastr = inject(ToastrService);
  private datePipe = inject(DatePipe);
  private cdr = inject(ChangeDetectorRef);

  dataInicial = '';
  dataFinal = '';

  produtos: RelatorioProdutoVendidoItem[] = [];
  carregado = false;
  carregando = false;
  tentouBuscar = false;
  dataGeracao = '';

  get dataInicialInvalida(): boolean {
    return this.tentouBuscar && this.dataInicial.trim() === '';
  }

  get dataFinalInvalida(): boolean {
    return this.tentouBuscar && this.dataFinal.trim() === '';
  }

  get totalM2Vendido(): number {
    return this.produtos.reduce((acc, p) => acc + (p.qtdM2VendidoMes ?? 0), 0);
  }

  buscar(): void {
    this.tentouBuscar = true;

    if (this.dataInicial.trim() === '' || this.dataFinal.trim() === '') {
      this.toastr.warning('Informe a data inicial e a data final para gerar o relatório.', 'Período obrigatório');
      return;
    }

    if (this.dataInicial > this.dataFinal) {
      this.toastr.warning('A data inicial deve ser anterior ou igual à data final.', 'Período inválido');
      return;
    }

    this.carregando = true;
    this.carregado = false;

    this.analyticsService.getRelatorioProdutosVendidos(this.dataInicial, this.dataFinal).subscribe({
      next: (data) => {
        this.produtos = data ?? [];
        this.dataGeracao = this.datePipe.transform(new Date(), 'dd/MM/yyyy HH:mm') ?? '';
        this.carregado = true;
        this.carregando = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.toastr.error('Erro ao carregar relatório de produtos vendidos.', 'Erro');
        this.carregando = false;
        this.cdr.detectChanges();
      }
    });
  }

  imprimir(): void {
    const esc = (value: string | number | null | undefined): string =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const num = (value: number, casas = 2) => (value != null ? formatarNumeroPtBR(value, casas) : '0');

    const periodLabel = `${this.formatDate(this.dataInicial)} a ${this.formatDate(this.dataFinal)}`;

    const rows = this.produtos.map((prod, i) => `
      <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
        <td>${esc(prod.nomeProduto)}</td>
        <td>${esc(prod.tipo)}</td>
        <td class="center">${esc(prod.dimensaoUnitariaM2)}</td>
        <td class="right">${num(prod.qtdM2VendidoMes)}</td>
        <td class="center">${esc(this.formatMesReferencia(prod.mesReferencia))}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Relatório de Produtos Vendidos</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #000; background: #fff; }

    @page {
      size: A4 landscape;
      margin: 14mm 12mm 18mm 12mm;
      @bottom-left   { content: "Relatório de Produtos Vendidos"; font-size: 7.5pt; color: #555; }
      @bottom-center { content: "Página " counter(page) " de " counter(pages); font-size: 7.5pt; color: #000; }
      @bottom-right  { content: "Gerado em: ${esc(this.dataGeracao)}"; font-size: 7.5pt; color: #555; }
    }

    /* Cabeçalho */
    .header { text-align: center; margin-bottom: 10px; }
    .header h1 { font-size: 16pt; font-weight: 700; letter-spacing: 0.5px; }
    .header .meta { font-size: 9pt; color: #444; margin-top: 4px; }
    .header .divider { border: none; border-top: 2px solid #000; margin-top: 8px; }

    /* Tabela */
    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    thead tr { background-color: #1a1a2e; color: #fff; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    thead th { padding: 6px 7px; font-size: 9pt; font-weight: 700; text-align: left; border: 1px solid #333; }
    tbody td { padding: 4px 7px; font-size: 9pt; border: 1px solid #ccc; vertical-align: middle; }
    tbody tr.even { background-color: #f5f5f5; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    tbody tr { page-break-inside: avoid; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }

    /* Totais */
    tfoot tr td { padding: 5px 7px; font-size: 9pt; font-weight: 700; border-top: 2px solid #000; border-bottom: 1px solid #ccc; background-color: #e0e0e0; print-color-adjust: exact; -webkit-print-color-adjust: exact; }

    /* Alinhamentos */
    .center { text-align: center; }
    .right  { text-align: right; }
    .bold   { font-weight: 700; }
    thead th.right  { text-align: right; }
    thead th.center { text-align: center; }

    /* Rodapé de resumo */
    .summary { margin-top: 14px; font-size: 9pt; color: #333; }
  </style>
</head>
<body>
  <div class="header">
    <h1>Relatório de Produtos Vendidos</h1>
    <div class="meta">
      Período: ${esc(periodLabel)} &nbsp;|&nbsp; Total: ${esc(this.produtos.length)} produto(s) &nbsp;|&nbsp; Gerado em: ${esc(this.dataGeracao)}
    </div>
    <hr class="divider"/>
  </div>

  <table>
    <thead>
      <tr>
        <th>Produto</th>
        <th>Tipo</th>
        <th class="center">Unidade Medida</th>
        <th class="right">Qtd. Vendido no Mês</th>
        <th class="center">Mês de Referência</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr>
        <td colspan="3" class="right">Total:</td>
        <td class="right">${num(this.totalM2Vendido)}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>

  <div class="summary">
    Total de m² vendidos no período: <strong>${num(this.totalM2Vendido)} m²</strong>
  </div>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=1100,height=750');
    if (!win) {
      this.toastr.warning('O navegador bloqueou a abertura da janela de impressão. Permita pop-ups e tente novamente.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  }

  formatDate(value: string | undefined): string {
    if (!value) return '';
    return this.datePipe.transform(value, 'dd/MM/yyyy') ?? value;
  }

  formatMesReferencia(value: string | undefined): string {
    if (!value) return '';
    const [ano, mes] = value.split('-');
    return `${mes}/${ano}`;
  }

  limpar(): void {
    this.dataInicial = '';
    this.dataFinal = '';
    this.produtos = [];
    this.carregado = false;
    this.tentouBuscar = false;
    this.cdr.detectChanges();
  }
}
